import { SerializedError } from '@reduxjs/toolkit';
import { fetchBaseQuery, createApi, FetchBaseQueryError, BaseQueryFn, FetchArgs } from '@reduxjs/toolkit/query/react';
import { getCorrectBearerJwtForUrl } from 'components/accessControl/getCorrectBearerJwtForUrl';
import { v4 as uuidv4 } from 'uuid';
import { eAPITags } from './eAPITags';
import { IEnvironmentVariablesResponse } from '../types/environment/IEnvironmentVariablesResponse';
import { decodeOurJWT } from 'components/accessControl/decodeOurJWT';
import { IAuthenticationMeDataType } from 'types/accessControl/IAuthenticationMeDataType';

const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const baseUrl = BaseAPI.endpoints.getEnvironmentVariables.select()(api.getState() as any).data?.apiLocation;
  const baseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { endpoint }) => {
      const url = typeof args === 'string' ? args : args.url;
      headers.set('authorization', `Bearer ${getCorrectBearerJwtForUrl(url ?? '')}`);
      headers.set('spruce-request-id', uuidv4());
      return headers;
    },
  });
  return baseQuery(args, api, extraOptions);
};

export const BaseAPI = createApi({
  baseQuery: dynamicBaseQuery,
  endpoints: (build) => ({
    getEnvironmentVariables: build.query<IEnvironmentVariablesResponse, void>({
      query: () => `//${window.location.host}/ui/envVariables.json`,
      providesTags: () => [eAPITags.EnvironmentVariables],
    }),
    getMe: build.query<IAuthenticationMeDataType, void>({
      query: () => ({
        url: '/timber/api/internal/auth/me',
        method: 'GET',
      }),
      transformResponse: (baseReturnValue: IAuthenticationMeDataType) => ({
        ...baseReturnValue,
        decryptedToken: baseReturnValue.jwt ? decodeOurJWT(baseReturnValue.jwt) : baseReturnValue.decryptedToken,
      }),
    }),
  }),
  tagTypes: Object.values(eAPITags),
});

export const { useGetEnvironmentVariablesQuery, useGetMeQuery } = BaseAPI;

export const convertToError = (error: FetchBaseQueryError | SerializedError | undefined): Error | undefined => {
  if (!error) {
    return error;
  }
  if ('status' in error) {
    switch (error.status) {
      case 'FETCH_ERROR':
      case 'CUSTOM_ERROR':
      case 'PARSING_ERROR':
        return new Error(error.error);
      case 400:
        return new Error((error.data as any).message);
      default:
        return new Error(`API Call failed with status code: ${error.status}`);
    }
  }
  return new Error(error.message);
};
